import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Loader2, CheckCircle2, AlertTriangle, Delete, Lock, Star } from "lucide-react";
import {
  readKioskSession, refreshKioskSession, kioskLogin, kioskLogout, doorLookup,
} from "../lib/kioskSession";

/**
 * NailEventDoorKiosk — venue-entrance self-service (Phase 8.7).
 *
 * One route, three modes:
 *   1. No ?k= on URL or invalid link_token → generic 404. PIN prompt does not
 *      render. That's the "no leaked-URL alone gets you to the PIN screen"
 *      requirement.
 *   2. Valid ?k=, no kiosk session → PIN entry (4 dots, auto-submit on 4th).
 *   3. Valid kiosk session → door keypad (10-digit phone → door endpoint).
 *
 * PIN entry error copy is intentionally sparse:
 *   * wrong PIN     → shake + "Mã PIN không đúng" (never say attempts left)
 *   * locked out    → "Vui lòng thử lại sau X giây" (X from server)
 * Success stores the token via saveKioskSession; a small Lock button re-arms
 * the PIN prompt without wiping other state.
 */

const KEYS = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "C", "0", "back"];
const AUTO_RESET_MS = 8000;
const SHAKE_MS = 600;
const ACCENT = "#22D3EE";

function formatPhone(digits) {
  if (!digits) return "";
  const d = digits.slice(0, 10);
  if (d.length <= 3) return `(${d}`;
  if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

// ------------------------------------------------------------------
// Reusable neutral-grey keypad. Used by both the PIN prompt and the
// door keypad so anyone shoulder-surfing sees exactly one visual pattern.
// ------------------------------------------------------------------
function Keypad({ onTap, disabled, ariaClear, ariaBack, testIdPrefix = "door" }) {
  return (
    <div
      className="w-full max-w-[420px] keypad-grid"
      data-testid={`${testIdPrefix}-keypad`}
    >
      {KEYS.map((k) => (
        <button
          key={k}
          type="button"
          disabled={disabled}
          onClick={() => onTap(k)}
          data-testid={`${testIdPrefix}-key-${k === "back" ? "back" : k.toLowerCase()}`}
          aria-label={k === "back" ? ariaBack : k === "C" ? ariaClear : k}
          className="keypad-key rounded-2xl font-semibold flex items-center justify-center transition-transform active:scale-[0.96] focus:outline-none focus-visible:ring-2 focus-visible:ring-white/40 bg-white/[0.04] hover:bg-white/[0.08] text-white border border-white/5 disabled:opacity-40"
        >
          {k === "back" ? <Delete className="w-6 h-6" /> : k}
        </button>
      ))}
    </div>
  );
}

// ------------------------------------------------------------------
// 4-dot progress row shown above the PIN keypad. Empty ring → filled dot.
// ------------------------------------------------------------------
function PinDots({ length, error }) {
  return (
    <div
      className={`w-full max-w-[280px] flex items-center justify-center gap-4 pin-dots ${error ? "shake" : ""}`}
      data-testid="pin-dots"
    >
      {[0, 1, 2, 3].map((i) => (
        <span
          key={i}
          data-testid={`pin-dot-${i}`}
          className={`inline-block rounded-full transition-all ${
            i < length ? "bg-white" : "bg-white/10 border border-white/20"
          }`}
          style={{ width: "clamp(14px, 2.6dvh, 22px)", height: "clamp(14px, 2.6dvh, 22px)" }}
        />
      ))}
    </div>
  );
}

// ------------------------------------------------------------------
// Root
// ------------------------------------------------------------------
export default function NailEventDoorKiosk() {
  const { slug } = useParams();
  const [params] = useSearchParams();
  const linkToken = params.get("k");
  const navigate = useNavigate();

  const lang = typeof window !== "undefined" && localStorage.getItem("nailEvent.lang") === "en" ? "en" : "vi";

  // No ?k= on the URL → render nothing but a generic Not Found. Do NOT prompt
  // for a PIN — a leaked slug alone must not reach the PIN screen.
  if (!linkToken || linkToken.length < 8) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-[#05070F] text-slate-300 text-center px-6">
        <div>
          <p className="text-[10px] uppercase tracking-[0.32em] text-slate-500 mb-3">404</p>
          <p className="text-lg font-serif text-slate-200">Not Found</p>
        </div>
      </div>
    );
  }

  return <KioskInner slug={slug} linkToken={linkToken} lang={lang} navigate={navigate} />;
}

function KioskInner({ slug, linkToken, lang, navigate }) {
  const [session, setSession] = useState(() => readKioskSession());
  const [checkingSession, setCheckingSession] = useState(!!readKioskSession());

  useEffect(() => {
    if (!session) return;
    (async () => {
      const fresh = await refreshKioskSession();
      // Session must be for this event; otherwise treat as unauth'd
      if (!fresh?.event?.slug || fresh.event.slug !== slug) {
        setSession(null);
        setCheckingSession(false);
        return;
      }
      setSession(fresh);
      setCheckingSession(false);
    })();
  }, [session, slug]);

  if (checkingSession) return <SplashScreen />;

  if (!session) {
    return (
      <PinScreen
        slug={slug}
        linkToken={linkToken}
        lang={lang}
        onSuccess={(s) => setSession(s)}
      />
    );
  }
  return (
    <DoorScreen
      slug={slug}
      lang={lang}
      onLock={() => { kioskLogout(); setSession(null); }}
      onUnauth={() => { setSession(null); navigate(`/nail-event/${slug}/checkin?k=${encodeURIComponent(linkToken)}`); }}
    />
  );
}

function SplashScreen() {
  return (
    <div className="door-kiosk-root fixed inset-0 flex items-center justify-center bg-[#05070F] text-slate-500">
      <Loader2 className="w-6 h-6 animate-spin" />
    </div>
  );
}

// ------------------------------------------------------------------
// PIN entry screen — reuses <Keypad>, 4 dots, auto-submit on 4th digit,
// shake on wrong PIN, generic error, exponential lockout copy from server.
// ------------------------------------------------------------------
function PinScreen({ slug, linkToken, lang, onSuccess }) {
  const [digits, setDigits] = useState("");
  const [error, setError] = useState(false);
  const [busy, setBusy] = useState(false);
  const [lockMsg, setLockMsg] = useState("");
  const shakeTimerRef = useRef(null);

  const wrongCopy = lang === "vi" ? "Mã PIN không đúng" : "Wrong PIN";
  const promptCopy = lang === "vi" ? "Nhập mã PIN của kiosk" : "Enter kiosk PIN";
  const ariaBack = lang === "vi" ? "Xóa một số" : "Backspace";
  const ariaClear = lang === "vi" ? "Xóa hết" : "Clear";

  const submit = useCallback(async (pin) => {
    setBusy(true); setError(false); setLockMsg("");
    try {
      const s = await kioskLogin({ event_slug: slug, pin, link_token: linkToken });
      onSuccess(s);
    } catch (e) {
      if (e?.response?.status === 429) {
        // Lockout — server-provided detail is generic ("Vui lòng thử lại sau X giây")
        setLockMsg(e.response?.data?.detail || (lang === "vi" ? "Vui lòng thử lại sau ít phút." : "Please try again later."));
      } else {
        setError(true);
        if (shakeTimerRef.current) clearTimeout(shakeTimerRef.current);
        shakeTimerRef.current = setTimeout(() => setError(false), SHAKE_MS);
      }
      setDigits("");
    } finally {
      setBusy(false);
    }
  }, [slug, linkToken, lang, onSuccess]);

  const tap = useCallback((key) => {
    if (busy || lockMsg) return;
    if (key === "C") { setDigits(""); return; }
    if (key === "back") { setDigits((d) => d.slice(0, -1)); return; }
    if (!/^[0-9]$/.test(key)) return;
    setDigits((d) => {
      if (d.length >= 4) return d;
      const next = d + key;
      if (next.length === 4) setTimeout(() => submit(next), 0);
      return next;
    });
  }, [busy, lockMsg, submit]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key >= "0" && e.key <= "9") tap(e.key);
      else if (e.key === "Backspace") tap("back");
      else if (e.key === "Escape") tap("C");
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [tap]);

  return (
    <div
      className="door-kiosk-root fixed inset-0 overflow-hidden text-slate-100 select-none"
      style={{
        background: "radial-gradient(1200px 700px at 50% -10%, #1B1F35 0%, #0B0E1A 55%, #05070F 100%)",
        paddingTop: "env(safe-area-inset-top)",
        paddingBottom: "env(safe-area-inset-bottom)",
        paddingLeft: "env(safe-area-inset-left)",
        paddingRight: "env(safe-area-inset-right)",
      }}
      data-testid="pin-screen"
    >
      <div className="w-full h-full flex flex-col items-center justify-between gap-2 px-4 py-3 sm:px-6 sm:py-4 body-layout">
        {/* Top: prompt + dots */}
        <div className="w-full max-w-[420px] flex flex-col items-center gap-3 shrink-0 col-display">
          <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.28em] text-slate-400 font-semibold">
            <Lock className="w-3 h-3" /> {promptCopy}
          </div>
          <PinDots length={digits.length} error={error} />
          <div className="h-6 flex items-center">
            {lockMsg
              ? <p data-testid="pin-lockout" className="text-amber-300 text-xs">{lockMsg}</p>
              : error
                ? <p data-testid="pin-error" className="text-rose-300 text-xs">{wrongCopy}</p>
                : <span className="text-slate-600 text-xs">&nbsp;</span>}
          </div>
        </div>

        {/* Keypad */}
        <Keypad onTap={tap} disabled={busy || !!lockMsg} ariaClear={ariaClear} ariaBack={ariaBack} testIdPrefix="pin" />

        {/* No CTA — auto-submit on the 4th digit. Preserve layout with a spacer. */}
        <div className="w-full max-w-[420px] shrink-0 col-cta h-[clamp(0.75rem,2dvh,1.15rem)]" />
      </div>

      <StyleBlock />
    </div>
  );
}

// ------------------------------------------------------------------
// Door keypad screen — same layout as before, unchanged spec:
// display / keypad / CTA, auto-submit on 10th digit, result replaces view.
// ------------------------------------------------------------------
function DoorScreen({ slug, lang, onLock, onUnauth }) {
  const placeholder = lang === "vi" ? "Nhập số điện thoại đã đăng ký" : "Enter your registered phone number";
  const ctaText = lang === "vi" ? "TÌM & CHECK-IN" : "SEARCH & CHECK-IN";
  const ctaLoading = lang === "vi" ? "Đang xử lý…" : "Processing…";
  const tapContinue = lang === "vi" ? "Nhấn để tiếp tục" : "Tap to continue";
  const seeOrgTitle = lang === "vi" ? "Gặp ban tổ chức" : "See the organizer";
  const seeOrgBody = lang === "vi"
    ? "Anh/chị vui lòng gặp ban tổ chức để hoàn tất check-in."
    : "Please see the organizer to complete check-in.";
  const successTitle = lang === "vi" ? "Xin chào" : "Welcome";
  const successBody = lang === "vi" ? "Đã check-in thành công." : "You're checked in.";
  const alreadyTitle = lang === "vi" ? "Đã check-in trước đó" : "Already checked in";
  const alreadyBody = lang === "vi" ? "Khách này đã có mặt." : "This guest has already checked in.";
  const ariaClear = lang === "vi" ? "Xóa hết" : "Clear";
  const ariaBack = lang === "vi" ? "Xóa một số" : "Backspace";

  const [digits, setDigits] = useState("");
  const [state, setState] = useState("idle");
  const [result, setResult] = useState(null);
  const resetTimerRef = useRef(null);
  const submittedRef = useRef(false);

  // Wake lock
  useEffect(() => {
    let wakeLock = null;
    const request = async () => {
      try { if ("wakeLock" in navigator) wakeLock = await navigator.wakeLock.request("screen"); } catch { /* ignore */ }
    };
    request();
    const onVis = () => { if (document.visibilityState === "visible") request(); };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      document.removeEventListener("visibilitychange", onVis);
      try { wakeLock && wakeLock.release && wakeLock.release(); } catch { /* ignore */ }
    };
  }, []);

  const reset = useCallback(() => {
    setDigits(""); setResult(null); setState("idle");
    submittedRef.current = false;
  }, []);
  const scheduleReset = useCallback(() => {
    if (resetTimerRef.current) clearTimeout(resetTimerRef.current);
    resetTimerRef.current = setTimeout(reset, AUTO_RESET_MS);
  }, [reset]);
  useEffect(() => () => { if (resetTimerRef.current) clearTimeout(resetTimerRef.current); }, []);

  const submit = useCallback(async (phoneDigits) => {
    const d = phoneDigits || digits;
    if (d.length !== 10 || submittedRef.current) return;
    submittedRef.current = true;
    setState("loading");
    try {
      const data = await doorLookup({ phone: `+1${d}`, language: lang });
      if (data.status === "checked_in") { setResult(data); setState("success"); }
      else if (data.status === "already_checked_in") { setResult(data); setState("already"); }
      else { setState("see_organizer"); }
    } catch (e) {
      if (e?.response?.status === 401) { onUnauth(); return; }
      setState("see_organizer");
    }
    scheduleReset();
  }, [digits, lang, onUnauth, scheduleReset]);

  const tap = useCallback((key) => {
    if (state !== "idle") return;
    if (key === "C") { setDigits(""); return; }
    if (key === "back") { setDigits((d) => d.slice(0, -1)); return; }
    if (!/^[0-9]$/.test(key)) return;
    setDigits((d) => {
      if (d.length >= 10) return d;
      const next = d + key;
      if (next.length === 10) setTimeout(() => submit(next), 0);
      return next;
    });
  }, [state, submit]);

  useEffect(() => {
    const onKey = (e) => {
      if (state !== "idle") return;
      if (e.key >= "0" && e.key <= "9") tap(e.key);
      else if (e.key === "Backspace") tap("back");
      else if (e.key === "Escape") tap("C");
      else if (e.key === "Enter" && digits.length === 10) submit();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [state, tap, submit, digits]);

  const overlay = useMemo(() => {
    if (state === "success") return { tone: "success", title: successTitle, body: successBody, name: result?.registration?.display_name || result?.registration?.business_name, is_vip: !!result?.registration?.is_vip };
    if (state === "already") return { tone: "neutral", title: alreadyTitle, body: alreadyBody, name: result?.registration?.display_name || result?.registration?.business_name, is_vip: !!result?.registration?.is_vip };
    if (state === "see_organizer") return { tone: "neutral", title: seeOrgTitle, body: seeOrgBody };
    return null;
  }, [state, result, successTitle, successBody, alreadyTitle, alreadyBody, seeOrgTitle, seeOrgBody]);

  const ready = digits.length === 10 && state === "idle";
  const showKeypad = state === "idle" || state === "loading";
  const showResult = state !== "idle" && state !== "loading";

  return (
    <div
      className="door-kiosk-root fixed inset-0 overflow-hidden text-slate-100 select-none"
      style={{
        background: "radial-gradient(1200px 700px at 50% -10%, #1B1F35 0%, #0B0E1A 55%, #05070F 100%)",
        paddingTop: "env(safe-area-inset-top)",
        paddingBottom: "env(safe-area-inset-bottom)",
        paddingLeft: "env(safe-area-inset-left)",
        paddingRight: "env(safe-area-inset-right)",
      }}
      data-testid="door-kiosk"
    >
      {/* Corner lock — deliberate lock-back-to-PIN. */}
      <button
        type="button"
        onClick={onLock}
        data-testid="door-lock"
        aria-label={lang === "vi" ? "Khoá" : "Lock"}
        className="absolute z-30 rounded-full bg-white/5 hover:bg-white/10 text-slate-300 hover:text-white transition-colors p-2 border border-white/10"
        style={{ top: "calc(env(safe-area-inset-top) + 8px)", right: "calc(env(safe-area-inset-right) + 8px)" }}
      >
        <Lock className="w-4 h-4" />
      </button>

      {showKeypad && (
        <div className="w-full h-full flex flex-col items-center justify-between gap-2 px-4 py-3 sm:px-6 sm:py-4 body-layout">
          <div className="w-full max-w-[420px] shrink-0 col-display">
            <div
              data-testid="door-phone-display"
              className={`w-full rounded-2xl bg-white/[0.04] border border-white/10 text-center display-fluid ${digits ? "text-white" : "text-slate-500"}`}
            >
              <span className="font-mono tracking-wider display-font">
                {digits ? formatPhone(digits) : placeholder}
              </span>
            </div>
          </div>

          <Keypad onTap={tap} disabled={state !== "idle"} ariaClear={ariaClear} ariaBack={ariaBack} testIdPrefix="door" />

          <button
            type="button"
            onClick={() => submit()}
            disabled={!ready}
            data-testid="door-submit"
            className={[
              "w-full max-w-[420px] inline-flex items-center justify-center gap-2 rounded-2xl font-semibold tracking-[0.18em] uppercase text-slate-900 submit-fluid shrink-0 col-cta transition-transform",
              ready ? "active:scale-[0.99] hover:brightness-110" : "opacity-40 cursor-not-allowed",
            ].join(" ")}
            style={{ background: ACCENT }}
          >
            {state === "loading" ? (
              <><Loader2 className="w-4 h-4 animate-spin" /> {ctaLoading}</>
            ) : ctaText}
          </button>
        </div>
      )}

      {showResult && overlay && (
        <button
          type="button"
          onClick={reset}
          data-testid={`door-result-${state}`}
          className="absolute inset-0 flex items-center justify-center px-6"
          aria-label={tapContinue}
        >
          <div className="max-w-md w-full text-center">
            {/* Phase 8.15 — VIP landing ribbon. Only shown on the kiosk
                confirmation card, never in the recorded roster. Soft gold so
                the tablet stays classy while hosts standing nearby can spot
                the VIP guest instantly. */}
            {overlay.is_vip && (
              <div
                data-testid="door-vip-ribbon"
                className="mx-auto mb-4 inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-[11px] font-bold uppercase tracking-[0.28em] text-amber-100 bg-gradient-to-r from-amber-400/25 via-amber-300/15 to-amber-400/25 ring-1 ring-amber-300/50 shadow-[0_0_24px_-6px_rgba(251,191,36,0.55)]"
              >
                <Star className="w-3.5 h-3.5 fill-amber-200" />
                {lang === "vi" ? "Khách VIP" : "VIP guest"}
              </div>
            )}
            <div className={[
              "mx-auto rounded-full flex items-center justify-center border-2 overlay-icon",
              overlay.tone === "success" && "bg-emerald-500/15 border-emerald-400 text-emerald-300",
              overlay.tone === "neutral" && "bg-white/5 border-white/20 text-slate-200",
            ].filter(Boolean).join(" ")}>
              {overlay.tone === "success"
                ? <CheckCircle2 className="w-8 h-8 sm:w-10 sm:h-10" />
                : <AlertTriangle className="w-8 h-8 sm:w-10 sm:h-10" />}
            </div>
            <h3 className="font-serif text-white mt-4 overlay-title">{overlay.title}</h3>
            {overlay.name && (
              <p data-testid="door-result-name" className="text-slate-200 mt-1.5 truncate overlay-name">{overlay.name}</p>
            )}
            <p className="text-slate-400 mt-2 overlay-body">{overlay.body}</p>
            <p className="text-slate-600 mt-6 text-[10px] tracking-[0.28em] uppercase">{tapContinue}</p>
          </div>
        </button>
      )}

      <StyleBlock />
    </div>
  );
}

function StyleBlock() {
  return (
    <style>{`
      .door-kiosk-root { height: 100dvh; }
      @supports not (height: 100dvh) { .door-kiosk-root { height: 100vh; } }
      .display-fluid { padding: clamp(0.7rem, 2.2dvh, 1.2rem) 1rem; }
      .display-font  { font-size: clamp(1.15rem, 3.2dvh, 2rem); }
      .submit-fluid  { padding: clamp(0.75rem, 2dvh, 1.15rem) 1rem; font-size: clamp(0.72rem, 1.7dvh, 0.95rem); }
      .keypad-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: clamp(0.4rem, 1.1dvh, 0.9rem); }
      .keypad-key { height: clamp(48px, 9dvh, 92px); font-size: clamp(1.35rem, 3.6dvh, 2.15rem); }
      .overlay-icon { width: clamp(56px, 8dvh, 80px); height: clamp(56px, 8dvh, 80px); }
      .overlay-title { font-size: clamp(1.75rem, 4.8dvh, 3rem); }
      .overlay-name  { font-size: clamp(1.05rem, 2.5dvh, 1.4rem); }
      .overlay-body  { font-size: clamp(0.85rem, 1.7dvh, 1rem); }
      @media (min-width: 640px) { .body-layout > * { max-width: 380px; } }
      @media (min-width: 1024px) { .body-layout > * { max-width: 420px; } }
      @media (orientation: landscape) and (max-height: 500px) {
        .body-layout {
          display: grid !important;
          grid-template-columns: 1fr 1fr;
          grid-template-rows: auto 1fr;
          grid-template-areas: "display keypad" "cta keypad";
          align-items: center; gap: 0.75rem;
        }
        .col-display { grid-area: display; align-self: end; max-width: 360px; }
        .col-cta { grid-area: cta; align-self: start; max-width: 360px; }
        .keypad-grid { grid-area: keypad; max-width: 320px; height: 100%; }
        .keypad-key { height: clamp(44px, 20dvh, 78px); }
      }
      .pin-dots.shake { animation: pin-shake 0.4s cubic-bezier(.36,.07,.19,.97) both; }
      @keyframes pin-shake {
        10%, 90% { transform: translateX(-2px); }
        20%, 80% { transform: translateX(4px); }
        30%, 50%, 70% { transform: translateX(-6px); }
        40%, 60% { transform: translateX(6px); }
      }
    `}</style>
  );
}
